import React, { useState, useEffect, useRef } from "react";
import { TabView, TabPanel } from "primereact/tabview";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { FileUpload } from "primereact/fileupload";
import { Toast } from "primereact/toast";
import { useEmployeesByFilter } from "presentation/hooks/use-query-employees";
import { useEquipmentTypeByFilterAll } from "presentation/hooks/use-query-equipment-type";
import {
  useEquipmentMovements,
  useCreateEquipmentMovementMutation,
  useUpdateEquipmentMovementMutation,
  useEquipmentDocuments,
  useCreateEquipmentDocumentMutation,
  useDeleteEquipmentDocumentMutation,
} from "presentation/hooks/use-query-equipments";
import { Equipment } from "domain/entities/equipment";
import Spinner from "presentation/components/Shared/Spinner";
import { useUserStore } from "presentation/stores/use-user-store";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import assetRepository from "infra/repositories/asset-repository";
import { useUploadAssetMutation } from "presentation/hooks/use-query-assets";
import EquipmentAttributesForm from "presentation/components/Equipment/EquipmentAttributesForm";
import EquipmentBaseTab from "presentation/components/Equipment/EquipmentBaseTab";
import DocumentViewerDialog from "presentation/components/Shared/DocumentViewerDialog";
import EquipmentDocumentsTab from "presentation/components/Equipment/EquipmentDocumentsTab";
import EquipmentGalleryDialog from "presentation/components/Equipment/EquipmentGalleryDialog";
import EquipmentMovementDialog from "presentation/components/Equipment/EquipmentMovementDialog";
import EquipmentMovementsTab from "presentation/components/Equipment/EquipmentMovementsTab";
import { PendingMovementPhoto, useEquipmentMovementFlow } from "presentation/components/Equipment/useEquipmentMovementFlow";
import MobileToast from "../../MobileToast";

interface EquipmentFormProps {
  selectedEquipment: Equipment;
  setSelectedEquipment: (eq: Equipment) => void;
  attributesObject: Record<string, any>;
  setAttributesObject: (attrs: Record<string, any>) => void;
  readOnlyBaseFields?: boolean;
  isAdmin?: boolean;
  showOnlyVehicles?: boolean;
}

const EquipmentForm: React.FC<EquipmentFormProps> = ({
  selectedEquipment,
  setSelectedEquipment,
  attributesObject,
  setAttributesObject,
  readOnlyBaseFields = false,
  isAdmin = false,
  showOnlyVehicles = false,
}) => {
  const dropdownLabelStyle = { marginTop: "-1rem", fontSize: "smaller" };
  const fixedTabContentHeight = "60vh";
  const tabContentStyle: React.CSSProperties = {
    minHeight: fixedTabContentHeight,
  };
  const toast = useRef<Toast>(null);
  const { user } = useUserStore();
  const isAdminOrPatrimonio =
    isAdmin ||
    user?.roles?.some((role) =>
      ["admin", "patrimonio"].includes(role.name.toLowerCase()),
    ) ||
    user?.profileType?.toLowerCase() === "admin";

  const { data: employeesResponse, isLoading: loadingEmployees } =
    useEmployeesByFilter({ limit: 200 });
  const { data: eqTypesResponse, isLoading: loadingTypes } =
    useEquipmentTypeByFilterAll({ limit: 100 } as any);

  const selectedType = eqTypesResponse?.data?.find(
    (t) => t.id === selectedEquipment?.equipmentTypeId,
  );
  const viaturaType = eqTypesResponse?.data?.find(
    (t) => t.name.toLowerCase() === "viatura",
  );

  // React Query queries/mutations for movements and documents
  const {
    data: movementsData,
    refetch: refetchMovements,
    isLoading: loadingMovements,
  } = useEquipmentMovements(selectedEquipment?.id, !!selectedEquipment?.id);
  const {
    data: docsData,
    refetch: refetchDocs,
    isLoading: loadingDocs,
  } = useEquipmentDocuments(selectedEquipment?.id, !!selectedEquipment?.id);

  const createMovementMutation = useCreateEquipmentMovementMutation();
  const updateMovementMutation = useUpdateEquipmentMovementMutation();
  const createDocMutation = useCreateEquipmentDocumentMutation();
  const deleteDocMutation = useDeleteEquipmentDocumentMutation();
  const uploadAssetMutation = useUploadAssetMutation();

  // Dialog & Form states for new movement
  const [isMovementDialogVisible, setIsMovementDialogVisible] = useState(false);
  const movementPhotosInputRef = useRef<HTMLInputElement>(null);

  // Document Viewer states
  const [isViewerVisible, setIsViewerVisible] = useState(false);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [documentTitle, setDocumentTitle] = useState("");
  const [loadingViewerUrl, setLoadingViewerUrl] = useState(false);

  // Gallery Viewer states
  const [isGalleryVisible, setIsGalleryVisible] = useState(false);
  const [galleryImages, setGalleryImages] = useState<
    { key: string; url?: string }[]
  >([]);
  const [activeGalleryIndex, setActiveGalleryIndex] = useState(0);
  const [loadingGallery, setLoadingGallery] = useState(false);
  const [activeMovementId, setActiveMovementId] = useState<string | null>(null);
  const addPhotosToMovementInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (
      showOnlyVehicles &&
      viaturaType?.id &&
      selectedEquipment?.id &&
      !selectedEquipment.equipmentTypeId
    ) {

      setSelectedEquipment({
        ...selectedEquipment,
        equipmentTypeId: viaturaType.id,
      });
    }
  }, [showOnlyVehicles, viaturaType, selectedEquipment, setSelectedEquipment]);

  const viewDocFile = async (fileKey: string, title: string) => {
    setLoadingViewerUrl(true);
    setIsViewerVisible(true);
    setDocumentTitle(title);
    try {
      const response = await assetRepository.getAssetUrl(fileKey);
      if (response?.url) {
        setDocumentUrl(response.url);
      }
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: "Erro ao abrir o ficheiro.",
      });
    } finally {
      setLoadingViewerUrl(false);
    }
  };

  const openGallery = async (photoKeys: string[], movementId: string) => {
    setLoadingGallery(true);
    setIsGalleryVisible(true);
    setActiveGalleryIndex(0);
    setActiveMovementId(movementId);
    try {
      const imagesWithUrls = await Promise.all(
        photoKeys.map(async (key) => {
          try {
            const res = await assetRepository.getAssetUrl(key);
            return { key, url: res?.url };
          } catch {
            return { key, url: undefined };
          }
        }),
      );
      setGalleryImages(
        imagesWithUrls.filter((img) => img.url) as {
          key: string;
          url: string;
        }[],
      );
    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: "Erro ao carregar a galeria de imagens.",
      });
    } finally {
      setLoadingGallery(false);
    }
  };

  const handleAddMorePhotosToMovement = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0 || !activeMovementId || !selectedEquipment.id)
      return;

    setLoadingGallery(true);
    try {
      const uploadedPaths = galleryImages.map((img) => img.key);

      for (const file of files) {
        const folder = `equipment/photos/${selectedEquipment.id}`;
        const response = await uploadAssetMutation.mutateAsync({
          folder,
          file,
        });
        if (response?.filePath) {
          uploadedPaths.push(response.filePath);
        }
      }

      updateMovementMutation.mutate(
        {
          id: selectedEquipment.id,
          movementId: activeMovementId,
          data: { photos: JSON.stringify(uploadedPaths) },
        },
        {
          onSuccess: async () => {
            toast.current?.show({
              severity: "success",
              summary: "Sucesso",
              detail: "Foto(s) adicionada(s) com sucesso.",
            });
            refetchMovements();
            // Recarrega as URLs de visualização da galeria sem fechá-la
            try {
              const imagesWithUrls = await Promise.all(
                uploadedPaths.map(async (key) => {
                  try {
                    const res = await assetRepository.getAssetUrl(key);
                    return { key, url: res?.url };
                  } catch {
                    return { key, url: undefined };
                  }
                }),
              );
              const validImages = imagesWithUrls.filter((img) => img.url) as {
                key: string;
                url: string;
              }[];
              setGalleryImages(validImages);
              // Define a foto adicionada mais recentemente como ativa
              if (validImages.length > 0) {
                setActiveGalleryIndex(validImages.length - 1);
              }
            } catch {
              console.error("Erro ao atualizar visualizações da galeria.");
            }
          },
          onError: (err: any) => {
            toast.current?.show({
              severity: "error",
              summary: "Erro",
              detail: err.message || "Erro ao adicionar fotos.",
            });
          },
        },
      );
    } catch {
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: "Erro ao fazer upload das novas fotos.",
      });
    } finally {
      setLoadingGallery(false);
      if (addPhotosToMovementInputRef.current) {
        addPhotosToMovementInputRef.current.value = "";
      }
    }
  };

  const fileUploadRef = useRef<FileUpload>(null);

  const handleUploadDoc = async (event: any) => {
    const file = event.files[0];
    if (!file || !selectedEquipment?.id) return;
    try {
      const folder = `equipment/documents/${selectedEquipment.id}`;
      const response = await uploadAssetMutation.mutateAsync({
        folder,
        file,
      });
      if (response?.filePath) {
        createDocMutation.mutate(
          {
            id: selectedEquipment.id,
            data: { name: file.name, fileKey: response.filePath },
          },
          {
            onSuccess: () => {
              toast.current?.show({
                severity: "success",
                summary: "Sucesso",
                detail: "Documento adicionado com sucesso.",
              });
              refetchDocs();
              fileUploadRef.current?.clear();
            },
            onError: (err: any) => {
              toast.current?.show({
                severity: "error",
                summary: "Erro",
                detail: err.message || "Erro ao associar documento.",
              });
              fileUploadRef.current?.clear();
            },
          },
        );
      } else {
        fileUploadRef.current?.clear();
      }
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: "Erro ao fazer upload do ficheiro.",
      });
      fileUploadRef.current?.clear();
    }
  };

  const handleDeleteDoc = (docId: string) => {
    confirmDialog({
      message: "Tem a certeza que deseja eliminar este documento?",
      header: "Confirmação de Eliminação",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      acceptLabel: "Sim",
      rejectLabel: "Não",
      accept: () => {
        deleteDocMutation.mutate(docId, {
          onSuccess: () => {
            toast.current?.show({
              severity: "success",
              summary: "Sucesso",
              detail: "Documento removido.",
            });
            refetchDocs();
          },
          onError: (err: any) => {
            toast.current?.show({
              severity: "error",
              summary: "Erro",
              detail: err.message || "Não foi possível remover este documento.",
            });
          },
        });
      },
    });
  };

  const filteredTypes =
    eqTypesResponse?.data?.filter((t) => {
      const isViatura = t.id === viaturaType?.id;
      return showOnlyVehicles ? isViatura : !isViatura;
    }) || [];

  const isBaseDataComplete =
    !!selectedEquipment.equipmentTypeId?.trim() &&
    !!selectedEquipment.brand?.trim() &&
    !!selectedEquipment.model?.trim() &&
    !!selectedEquipment.serialOrCarRegistration?.trim();

  const {
    movementType,
    setMovementType,
    movementEmployeeId,
    setMovementEmployeeId,
    movementJustification,
    setMovementJustification,
    movementOdometerKm,
    setMovementOdometerKm,
    movementPhotos,
    movementPendingPhotos,
    uploadingPhotos,
    resetMovementDialogState,
    handleAddMovementPhotos,
    handleRemovePendingMovementPhoto,
    clearMovementPhotos,
    handleSaveMovement,
  } = useEquipmentMovementFlow({
    selectedEquipment,
    selectedType,
    isBaseDataComplete,
    setSelectedEquipment,
    createMovement: (params) => createMovementMutation.mutateAsync(params),
    refetchMovements,
    closeDialog: () => setIsMovementDialogVisible(false),
    showToast: (config) => toast.current?.show(config),
  });

  if (loadingEmployees || loadingTypes) {
    return <Spinner />;
  }

  return (
    <div className="card w-full">
      <MobileToast ref={toast} />
      <TabView>
        <TabPanel header="Base" leftIcon="pi pi-info-circle mr-2">
          <EquipmentBaseTab
            selectedEquipment={selectedEquipment}
            setSelectedEquipment={setSelectedEquipment}
            filteredTypes={filteredTypes}
            readOnlyBaseFields={readOnlyBaseFields}
            showOnlyVehicles={showOnlyVehicles}
            hasVehicleType={!!viaturaType}
            tabContentStyle={tabContentStyle}
            dropdownLabelStyle={dropdownLabelStyle}
            isAdmin={isAdmin}
          />
        </TabPanel>

        <TabPanel
          header="Mais Características"
          leftIcon="pi pi-sliders-h mr-2"
        >
          <EquipmentAttributesForm
            selectedEquipment={selectedEquipment}
            attributesObject={attributesObject}
            setAttributesObject={setAttributesObject}
            isAdmin={isAdmin}
            tabContentStyle={tabContentStyle}
            dropdownLabelStyle={dropdownLabelStyle}
          />
        </TabPanel>

        {isAdmin && (
          <TabPanel header="Movimentos & Histórico" leftIcon="pi pi-history mr-2">
            <EquipmentMovementsTab
              selectedEquipment={selectedEquipment}
              isBaseDataComplete={isBaseDataComplete}
              loadingMovements={loadingMovements}
              movements={movementsData?.data || []}
              tabContentStyle={tabContentStyle}
              isAdminOrPatrimonio={isAdminOrPatrimonio}
              onOpenNewMovement={() => {
                if (!selectedEquipment.id && !isBaseDataComplete) {
                  toast.current?.show({
                    severity: "warn",
                    summary: "Dados Base Obrigatórios",
                    detail:
                      "Preencha os campos obrigatórios da tab Base para adicionar um movimento na criação.",
                  });
                  return;
                }

                setMovementType("DELIVERY");
                setIsMovementDialogVisible(true);
              }}
              onOpenGallery={openGallery}
            />
          </TabPanel>
        )}

        {isAdmin && (
          <TabPanel header="Documentos Adicionais" leftIcon="pi pi-file-pdf mr-2">
            <EquipmentDocumentsTab
              equipmentId={selectedEquipment.id}
              loadingDocs={loadingDocs}
              documents={docsData?.data || []}
              userEmail={user?.email}
              isAdminOrPatrimonio={isAdminOrPatrimonio}
              tabContentStyle={tabContentStyle}
              fileUploadRef={fileUploadRef}
              onUploadDoc={handleUploadDoc}
              onViewDoc={viewDocFile}
              onDeleteDoc={handleDeleteDoc}
            />
          </TabPanel>
        )}
      </TabView>

      <EquipmentMovementDialog
        visible={isMovementDialogVisible}
        movementType={movementType}
        movementEmployeeId={movementEmployeeId}
        movementJustification={movementJustification}
        movementOdometerKm={movementOdometerKm}
        movementPhotos={movementPhotos}
        movementPendingPhotos={movementPendingPhotos}
        uploadingPhotos={uploadingPhotos}
        selectedType={selectedType}
        employees={employeesResponse?.data || []}
        dropdownLabelStyle={dropdownLabelStyle}
        movementPhotosInputRef={movementPhotosInputRef}
        onHide={() => {
          setIsMovementDialogVisible(false);
          resetMovementDialogState();
        }}
        onSave={handleSaveMovement}
        onMovementTypeChange={(nextMovementType) => {
          setMovementType(nextMovementType);
          clearMovementPhotos();
        }}
        onMovementEmployeeChange={setMovementEmployeeId}
        onMovementJustificationChange={setMovementJustification}
        onMovementOdometerKmChange={setMovementOdometerKm}
        onAddMovementPhotos={handleAddMovementPhotos}
        onPreviewPendingPhoto={(photo) => {
          setDocumentTitle(photo.fileName);
          setDocumentUrl(photo.previewUrl);
          setIsViewerVisible(true);
        }}
        onRemovePendingMovementPhoto={handleRemovePendingMovementPhoto}
      />

      <DocumentViewerDialog
        visible={isViewerVisible}
        title={documentTitle}
        documentUrl={documentUrl}
        loading={loadingViewerUrl}
        onHide={() => {
          setIsViewerVisible(false);
          setDocumentUrl(null);
        }}
      />

      <EquipmentGalleryDialog
        visible={isGalleryVisible}
        loading={loadingGallery}
        images={galleryImages}
        activeIndex={activeGalleryIndex}
        isAdminOrPatrimonio={isAdminOrPatrimonio}
        addPhotosInputRef={addPhotosToMovementInputRef}
        onHide={() => {
          setIsGalleryVisible(false);
          setGalleryImages([]);
          setActiveMovementId(null);
        }}
        onAddPhotos={handleAddMorePhotosToMovement}
        onSelectImage={setActiveGalleryIndex}
      />
      <ConfirmDialog />
    </div>
  );
};

export default EquipmentForm;
